/**
 * Every dashboard mutation goes through here, so that "who changed what" cannot
 * be forgotten in one place.
 *
 * Two rules this file enforces:
 *   1. State changes are conditional on the state the UI saw (`.eq('state', from)`),
 *      so two open tabs cannot both approve the same draft.
 *   2. Every accepted change writes an audit row. Audit failures are logged, not
 *      thrown: losing an audit line is bad, but silently failing to record a
 *      rejection the operator just made is worse.
 */

import { supabase } from './supabase';
import type { DraftPromptOverrides, DraftState, Settings } from './types';

export interface ActionResult {
  ok: boolean;
  error?: string;
}

export async function writeAudit(
  actor: string,
  action: string,
  entity: string,
  entityId: string | null,
  detail: Record<string, unknown> = {},
): Promise<void> {
  const { error } = await supabase
    .from('audit_log')
    .insert({ actor, action, entity, entity_id: entityId, detail });
  if (error) console.error('audit write failed', error.message);
}

async function transitionDraft(args: {
  draftId: string;
  from: DraftState;
  to: DraftState;
  actor: string;
  patch?: Record<string, unknown>;
  detail?: Record<string, unknown>;
}): Promise<ActionResult> {
  const { data, error } = await supabase
    .from('drafts')
    .update({ ...(args.patch ?? {}), state: args.to })
    .eq('id', args.draftId)
    .eq('state', args.from)
    .select('id');

  if (error) return { ok: false, error: error.message };
  if (!data || data.length === 0) {
    return { ok: false, error: `This draft is no longer "${args.from}". Reload and try again.` };
  }

  await writeAudit(args.actor, `draft.${args.to}`, 'draft', args.draftId, {
    from: args.from,
    to: args.to,
    ...(args.detail ?? {}),
  });
  return { ok: true };
}

/**
 * Put a draft back in the queue.
 *
 * This is the undo path, and it exists because every negative action here used to be permanent.
 * Removing a draft moves it to a state the queue does not query, so a misclick on a quiet button
 * would silently delete the row from view with no way back. Reopening from the toast is the same
 * transition as reopening from the history view.
 *
 * `sent` is deliberately not reopenable: a delivery row exists for it, and caps, gaps and the
 * survival check all read that row. Un-sending is a correction, not an undo.
 */
export async function reopenDraft(
  draftId: string,
  from: DraftState,
  actor: string,
): Promise<ActionResult> {
  return transitionDraft({
    draftId,
    from,
    to: 'pending',
    actor,
    patch: { rejected_reason: null },
  });
}

/**
 * Take a draft out of the queue - the card's Remove button.
 *
 * The UI says Remove and the database says `skipped`, and that gap is deliberate: the state value
 * and the audit action (`draft.skipped`) predate the button, and renaming either would mean a
 * migration plus a rewrite of the audit trail for a label. The queue shows nothing in this state,
 * the Rejected view shows it, and `reopenDraft` is the way back.
 *
 * Nothing is posted, sent or deleted. This is a decision about a draft; the stranger's post is
 * untouched and no delivery row is written, so it cannot move the caps.
 */
export async function removeDraft(
  draftId: string,
  from: DraftState,
  actor: string,
): Promise<ActionResult> {
  return transitionDraft({ draftId, from, to: 'skipped', actor });
}

/**
 * The card's Auto post button - hand this reply to the local poster (7 Oct 2026).
 *
 * `approved` is what the poster's `assist_only` mode is waiting for: `deliver/facebook.ts` treats
 * a queued draft as the human approval the guardrail asks for, so queueing one draft is what lets
 * it go without switching the whole lane to `auto`. It queues; it does not send - the poster does
 * that from the operator's PC, which is why the toast says so rather than claiming anything was
 * posted.
 */
export async function queueForAutoPost(
  draftId: string,
  from: DraftState,
  actor: string,
): Promise<ActionResult> {
  return transitionDraft({ draftId, from, to: 'approved', actor, detail: { via: 'auto post' } });
}

/** Take a queued draft back out - the card's Cancel auto post. Nothing has been sent yet. */
export async function unqueueAutoPost(
  draftId: string,
  from: DraftState,
  actor: string,
): Promise<ActionResult> {
  return transitionDraft({
    draftId,
    from,
    to: 'pending',
    actor,
    detail: { via: 'auto post cancelled' },
  });
}

export async function saveDraftBody(
  draftId: string,
  actor: string,
  body: string,
  original: string,
): Promise<ActionResult> {
  const { error } = await supabase.from('drafts').update({ edited_body: body }).eq('id', draftId);
  if (error) return { ok: false, error: error.message };

  await writeAudit(actor, 'draft.edited', 'draft', draftId, {
    originalLength: original.length,
    editedLength: body.length,
    changed: body.trim() !== original.trim(),
  });
  return { ok: true };
}

/**
 * P2.10 - the assisted half of delivery: the human has posted the reply themselves.
 *
 * The other half is the local poster (7 Oct 2026, P2.18), which records through the Worker's
 * `/admin/deliver/facebook/result`; this function is what the queue's Posted button still does,
 * and it is the fallback for anything the poster could not do.
 *
 * Order matters, and it is the opposite of what feels natural. The state transition goes
 * first because it is the only conditional write here (`.eq('state', from)`), so it is
 * what makes a double-click, or two open tabs, produce one delivery rather than two.
 * Inserting the `deliveries` row first and transitioning second would leave a delivery
 * behind every time somebody raced themselves.
 *
 * What this does NOT do: send anything, check the platform, or verify the reply is
 * really there. This dashboard cannot post, and it cannot read the thread back
 * without being asked to (that is the survival check in P2.11, which re-runs the actor
 * against the stored URL an hour later). This records a human's claim, on their name.
 *
 * `edited_body` is written here rather than only by the explicit "Save edit" button that used to
 * sit next to this one. Requiring that click meant an operator could edit a reply, post the edited
 * text, and leave `edited_body` null while the audit row said `edited: true` - the record of what
 * went out disagreeing with what went out.
 */
export async function markSent(
  draftId: string,
  from: DraftState,
  actor: string,
  body: string,
  edited: boolean,
): Promise<ActionResult> {
  const transition = await transitionDraft({
    draftId,
    from,
    to: 'sent',
    actor,
    patch: { edited_body: edited ? body : null },
    detail: { mode: 'assisted', edited, length: body.length },
  });
  if (!transition.ok) return transition;

  const postedAt = new Date().toISOString();
  const { error: deliveryError } = await supabase.from('deliveries').insert({
    draft_id: draftId,
    mode: 'assisted',
    posted_at: postedAt,
    posted_by: actor,
  });

  if (deliveryError) {
    // The draft is now `sent` but nothing recorded what went out, which matters more than
    // a tidy audit trail: caps, the min-gap rule and survival checks all read `deliveries`.
    await writeAudit(actor, 'delivery.record_failed', 'draft', draftId, {
      error: deliveryError.message,
      postedAt,
    });
    return {
      ok: false,
      error: `Marked as sent, but the delivery record failed (${deliveryError.message}). Add it by hand or the caps will be wrong.`,
    };
  }

  await writeAudit(actor, 'delivery.assisted', 'draft', draftId, { postedAt, postedBy: actor });
  return { ok: true };
}

export async function updateSettings(actor: string, patch: Partial<Settings>): Promise<ActionResult> {
  const { error } = await supabase
    .from('settings')
    .update({ ...patch, updated_by: actor })
    .eq('id', 1);
  if (error) return { ok: false, error: error.message };

  await writeAudit(actor, 'settings.updated', 'settings', '1', patch as Record<string, unknown>);
  return { ok: true };
}

export async function updateSource(
  actor: string,
  sourceId: string,
  patch: Record<string, unknown>,
): Promise<ActionResult> {
  const { error } = await supabase.from('sources').update(patch).eq('id', sourceId);
  if (error) return { ok: false, error: error.message };

  await writeAudit(actor, 'source.updated', 'source', sourceId, patch);
  return { ok: true };
}

/**
 * Save one lane's reply prompt, or clear it back to the built-in text.
 *
 * `body === null` means "use the built-in prompt again" - not "give the drafter no rules". The
 * Worker's `resolveDraftPrompt()` treats a blank string as absent, so both routes end in the
 * same place; writing null here means the row records the decision rather than an empty edit.
 *
 * Deliberately NOT routed through `updateSettings()` beside it, and the reason is the audit log.
 * That function writes its patch verbatim into the audit row's `detail`, which is right for a
 * boolean and wrong for a prompt's worth of prose: every future audit line would carry the full
 * text. This writes the same column either way and records `{ chars }` instead - small enough to
 * read, specific enough to answer "who changed it, and when".
 *
 * There is no read-modify-write any more. There used to be, because one jsonb object held three
 * lanes and supabase-js cannot merge a single key, so two tabs saving two lanes could lose one.
 * With one prompt the whole object is replaced, and the last save wins on purpose.
 */
export async function savePrompt(actor: string, body: string | null): Promise<ActionResult> {
  const next: DraftPromptOverrides = body === null ? {} : { prompt: body };

  const { error } = await supabase
    .from('settings')
    .update({ prompt_overrides: next, updated_by: actor })
    .eq('id', 1);
  if (error) return { ok: false, error: error.message };

  await writeAudit(
    actor,
    body === null ? 'settings.prompt_reset' : 'settings.prompt_saved',
    'settings',
    '1',
    { chars: body?.length ?? 0 },
  );
  return { ok: true };
}

export async function createSource(
  actor: string,
  input: {
    platform: string;
    identifier: string;
    display_name: string | null;
    link_allowed: boolean;
    solicitation_allowed: boolean;
    daily_cap: number;
    min_minutes_between_replies: number;
    notes: string | null;
    enabled: boolean;
  },
): Promise<ActionResult> {
  const { data, error } = await supabase
    .from('sources')
    .insert(input)
    .select('id');
  if (error) return { ok: false, error: error.message };

  await writeAudit(actor, 'source.created', 'source', data?.[0]?.id ?? null, input);
  return { ok: true };
}